import AL from "../../libs/appLib.mjs";
import {Glob as G } from "../../libs/Glob.mjs";
import autoFormFieldHelper from "./autoFormFieldHelper.mjs";

/** @typedef {import("./autoFormFieldHelper").AutoFormFieldHelperObject } */

/**
 * This is the main class to be used for helper(s)
 * @function
 * @type {AutoFormHelperClass}
 * @implements {AutoFormHelperObject}
 * @see {AutoFormHelperInit}
 * @see {AutoFormHelperObject}
 */
const autoFormHelper = class {

	/**
	 * Internal properties of autoFormHelper object
	 * @private
	 * @type {object}
	 * @property { string } [itemID]
	 * @property { string } [groupID]
	 * @property { object } [group] Group info object
	 * @property { Object[] } fields Fields definition
	 * @property {object} [data] The saved data for this item
	 * @property {AutoFormFieldHelperObject} [parent] The parent field, for sub-forms
	 * @property {number} [index] Filed index for parent, if multiple
	 * @property {boolean} [multi] Parent field is multiple?
	 * @property {function} [notify] Notification for changes in value
	 */
	#props = {};

	/**
	 * @param {AutoFormHelperInit} options if a string is expected to be itemID
	 * @param {function} [callback] Call back this function after load.
	 */
	constructor(options, callback) {
		if ( typeof options === "string" ) options = { itemID: String(options)};
		if ( Array.isArray(options.fields) ) this.#props.fields = G.clone(options.fields);
		if ( typeof options.data !== 'undefined') this.#props.data = G.clone(options.data);
		if ( typeof options.notify === 'function') this.#props.notify = options.notify;
		if ( Array.isArray(options.fields)) this.#props.fields = options.fields;

		if ( options.parent ) {
			this.#props.parent = options.parent;
			if ( typeof options.index === 'number') {
				this.#props.index = options.index;
				this.#props.multi = true;
			}
		} else {
			this.#props.itemID = options.itemID;
			this.#props.groupID = options.groupID || AL.group();
			if ( typeof options.group === 'object') this.#props.group = G.clone(options.group);
		}
		setTimeout( async ()=>{
			await this.#loadData();
			this.checkDependencies();
			if ( typeof callback === "function") {
				setTimeout( ()=>{ callback( !! this.#props.fields.length ) } );
			}
		});
	}

	/**
	 * Load item data and form definition
	 * @private
	 * @return {Promise<boolean>} Actually loaded, or not
	 */
	async #loadData() {
		/* Loaded yet */
		if ( typeof this.#props.data !== "undefined" ) return false;
		if ( this.#props.parent ) {
			/* We're in a sub-form */
			if ( this.#props.multi ) {
				this.#props.data = this.#props.parent.getMultiValue( this.#props.index ) || {};
			} else {
				this.#props.data = this.#props.parent.getValue() || {};
			}
		} else {
			/* Load by itemID/group */
			const fi = await AL.getFormInfo( this.#props.groupID );
			this.#props.group = fi?.group;
			if ( ! this.#props.fields ) {
				this.#props.fields = fi?.formDefinition?.fields || [];
			}
			if ( typeof this.#props.data === 'undefined' ) {
				this.#props.data = (await AL.getItemData( this.#props.itemID, this.#props.groupID )) || {};
			}
		}
		return true;
	}

	/**
	 * Check all dependencies
	 * @return {number} The changes
	 */
	checkDependencies() {
		if ( this.#props.checkingDependencies ) return;
		this.#props.checkingDependencies = true;
		const count = this.#checkReadonly() + this.#checkHidden();
		delete this.#props.checkingDependencies;
		return count;
	}

	/**
	 * Recursively check all hidden dependencies
	 * @private
	 * @return {number} The changes
	 */
	#checkHidden() {
		let count = 0;
		this.#props.fields.forEach((f, idx) => {
			if (!f.depends) return;
			if (!f.depends.find(d => d?.type !== 'readonly')) return;
			const fh = this.fieldHelper(f.key || idx);
			let show = false;
			f.depends.filter(d => d?.type !== 'readonly').forEach(d => {
				show = show || this.#evalDep(d);
			});
			if (show === fh.hidden()) {
				count++;
				fh.hidden(! show );
			}
		});
		if ( count > 0 ) count += this.#checkHidden();
		return count;
	}

	/**
	 * Check all readonly dependencies
	 * @private
	 * @return {number} The changes
	 */
	#checkReadonly() {
		let count = 0;
		this.#props.fields.forEach((f, idx) => {
			if (!f.depends) return;
			if (!f.depends.find(d => d?.type === 'readonly')) return;
			const fh = this.fieldHelper(f.key || idx);
			let ro = false;
			f.depends.filter(d => d.type === 'readonly').forEach(d => {
				ro = ro || this.#evalDep(d);
			});
			if (ro !== fh.readonly()) {
				count++;
				fh.readonly(ro);
			}
		});
		return count;
	}

	/**
	 * Evaluate dependency
	 * @private
	 * @param {object|string} dep
	 * @return {boolean}
	 */
	#evalDep( dep ) {
		if ( typeof dep === 'string' ) dep = { on: dep };
		const v = this.#props.data[dep.on];
		if ( typeof dep.value === 'string' ) {
			if ( Array.isArray(v) ) return v.map( x => String(x) ).includes(dep.value);
			return ( String(v) === dep.value);
		}
		if ( typeof dep.notvalue === 'string' ) {
			if ( Array.isArray(v) ) return ! v.map( x => String(x) ).includes(dep.value);
			return ( String(v) !== dep.value);
		}
		return ( typeof v !== "undefined" && v !== 0);
	}

	/**
	 * Build the form helper (if any) for a given fieldNode (id)
	 * @param {string|number} fieldNode The field id or field index
	 * @return {AutoFormFieldHelperObject|undefined}
	 */
	fieldHelper( fieldNode ) {
		let field;
		if (typeof fieldNode === "string") {
			field = this.#props.fields.find( f => f.key === fieldNode );
		} else if ( typeof fieldNode === 'number' ) {
			field = this.#props.fields[fieldNode];
		}
		if ( field ) {
			if ( ! field.fieldHelper ) {
				const args = { def: field, form: this };
				if ( typeof fieldNode === 'string' ) args.value = G.clone(this.#props.data[fieldNode]);
				field.fieldHelper = new autoFormFieldHelper(args);
			}
			return field.fieldHelper;
		}
	}

	/**
	 * Get whole form value
	 * @return {object}
	 */
	getData() { return G.clone( this.#props.data ); }

	/**
	 * Get index of multiple fields sub-forms
	 * @return {number|undefined}
	 */
	getIndex() { return this.#props.index }

	/**
	 * Get a field value
	 * @param {string} key Field ID
	 * @return {any}
	 */
	getValue( key ) { return G.clone( this.#props.data[key] ); }

	/**
	 * Perform actual notification
	 * @private
	 * @function
	 * @return {void}
	 */
	#notify() {
		if ( this.#props.checkingDependencies ) return;
		if ( typeof this.#props.notify === 'function') {
			this.#props.notify( G.clone( this.#props.data ) );
		}
		if ( this.#props.parent ) {
			this.#props.parent.notify( true );
		}
	}

	/**
	 * Set/unset/trigger field notification function
	 * @param {function|true|undefined} func The function for notification, true to perform notification
	 */
	notify( func ) {
		if ( typeof func === 'function') {
			this.#props.notify = func;
		} else if ( func ===  true ) {
			this.#notify();
		} else {
			delete this.#props.notify;
		}
	}

	/**
	 * Propagate data changes to parent, if any
	 * @private
	 * @return {void}
	 */
	#data2parent() {
		if ( this.#props.parent ) {
			if ( this.#props.multi ) {
				this.#props.parent.setMultiValue( this.#props.index, G.clone(this.#props.data), true );
			} else {
				this.#props.parent.setValue( G.clone(this.#props.data), true );
			}
		}
	}

	/**
	 * Set the whole form data
	 * @param {object} data
	 * @param {boolean} [isControl] This is a control field: don't notify, don't check dependencies
	 * @return {void}
	 */
	setData( data, isControl=false) {
		this.#props.data = G.clone(data);
		if ( ! isControl ) {
			this.checkDependencies();
			this.#notify();
			this.#data2parent();
		}
	}

	/**
	 * Set index of multiple fields sub-forms
	 * @param {number} index
	 * @return {void}
	 */
	setIndex( index ) {
		if ( this.#props.multi ) {
			this.#props.index = index;
			this.#data2parent( true );
		}
	}

	/**
	 * Set a field value
	 * @param {string} key Field ID
	 * @param {any} value Field value, undefined to delete it
	 * @param {boolean} [isControl] This is a control field: don't notify, don't check dependencies
	 * @return {void}
	 */
	setValue( key, value, isControl = false ) {
		const ov = G.clone(this.#props.data[key]);
		if ( typeof value === "undefined") {
			delete this.#props.data[key];
		} else {
			this.#props.data[key] = G.clone(value);
		}
		if ( ! isControl && ov !== this.#props.data[key] ) {
			this.checkDependencies();
			this.#notify();
			this.#data2parent();
		}
	}
}

export default autoFormHelper;
